import { resolveCssVariable, theme } from '@prophecy/theme';
import { useFocusTrap } from '@prophecy/utils/react/focus';
import { useControlledState } from '@prophecy/utils/react/hooks';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Provider, Root, Portal, Trigger } from '@radix-ui/react-tooltip';
import { isNil } from 'lodash-es';
import React, { isValidElement, ReactNode, Suspense } from 'react';

import { HOVER_CONTAIN_SELECTOR } from '../constants';
import { Stack } from '../Layout';
import { StyledArrow, StyledContent, StyledCopyIcon, StyledDetail, StyledMarkDown, tokens } from './styled';
import { TooltipProps, UseMarkdownFor } from './types';
export * from './constants';
export * from './types';

// `:focus-visible` cannot stand in for this: a programmatically focused element inherits it from
// the one focus came from, so a menu closing under the pointer still matches. Only the modality of
// the last real interaction tells a tab-to-the-trigger apart from a click somewhere else.
let keyboardWasLast = false;
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', () => (keyboardWasLast = true), true);
  document.addEventListener('pointerdown', () => (keyboardWasLast = false), true);
}

export function Tooltip({
  title,
  detail,
  visible,
  onVisibleChange,
  children,
  overlayClassName,
  overlayStyle,
  placement: side = 'top',
  placementOffset: sideOffset = 8,
  align,
  hasCopy = false,
  alignOffset,
  delay = 100,
  disabled,
  isMarkdown = UseMarkdownFor.none,
  forceTooltipContentColor = true,
  showArrow = false,
  portalled = true,
  ...contentProps
}: TooltipProps) {
  const [_visible, toggleVisible] = useControlledState({
    value: (!title && !detail) || disabled ? false : visible,
    onChange: onVisibleChange
  });

  const titleRef = React.createRef<HTMLSpanElement>();
  const detailsRef = React.createRef<HTMLSpanElement>();

  const container = useFocusTrap(Boolean(_visible));

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = titleRef?.current?.innerText || '';
    const details = detailsRef?.current?.innerText || '';
    await navigator.clipboard.writeText(`${text}\n${details}`);
  };

  // If child has aria-label, set aria-describedby to undefined to prevent duplicate screen reading
  const hasAriaLabel = isValidElement(children) && children.props?.['aria-label'];
  const triggerProps = hasAriaLabel ? { 'aria-describedby': undefined } : {};

  // stopPropagation on the child can't stop the open (Radix binds its handlers in capture mode)
  // so we check the element marker on the target element, to avoid opening the tooltip.

  const onTriggerPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    const contained = target?.closest?.(HOVER_CONTAIN_SELECTOR);
    if (!contained) return;

    // We suppress in two cases: the contained element is a DOM child of this trigger (standard),
    // or the event came from a React portal (outside the DOM but still in the React tree).
    // For portals, event.currentTarget.contains(contained) is always false,
    // so we use !targetInTrigger to detect that case.
    const targetInTrigger = event.currentTarget.contains(target);
    if (!targetInTrigger || event.currentTarget.contains(contained)) {
      event.preventDefault();
    }
  };

  // Radix opens on any focus, so a menu or dialog handing focus back to its trigger on close pops
  // the tooltip with neither hover nor keyboard. preventDefault stops Radix's composed handler.
  const onTriggerFocus = (event: React.FocusEvent<HTMLElement>) => {
    if (!keyboardWasLast) event.preventDefault();
  };

  const content = (
    <FocusScope
      asChild
      trapped={false}
      onUnmountAutoFocus={(e) => e.preventDefault()}
      onMountAutoFocus={(e) => e.preventDefault()}>
      <StyledContent
        className={overlayClassName}
        $forceTooltipContentColor={forceTooltipContentColor}
        style={overlayStyle}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        ref={container}
        tabIndex={-1}
        {...contentProps}
        data-test-id='tooltip-content'>
        <Stack direction='vertical'>
          {title && (
            <Stack direction='horizontal' width='100%' align='space-between' gap={theme.spaces.x4}>
              <span ref={titleRef}>
                {(isMarkdown === UseMarkdownFor.title || isMarkdown === UseMarkdownFor.both) &&
                typeof title === 'string' ? (
                  <Suspense fallback={title}>
                    <StyledMarkDown>{title}</StyledMarkDown>
                  </Suspense>
                ) : (
                  title
                )}
              </span>
              {hasCopy && <StyledCopyIcon size='xs' type='default' onClick={onCopy} />}
            </Stack>
          )}
          {detail && (
            <Stack direction='horizontal' width='100%' align='left'>
              <StyledDetail ref={detailsRef} style={{ paddingTop: 'none' }}>
                {(isMarkdown === UseMarkdownFor.detail || isMarkdown === UseMarkdownFor.both) &&
                typeof detail === 'string' ? (
                  <Suspense fallback={detail}>
                    <StyledMarkDown>{detail}</StyledMarkDown>
                  </Suspense>
                ) : (
                  detail
                )}
              </StyledDetail>
              {hasCopy && !title && <StyledCopyIcon size='xs' type='default' onClick={onCopy} />}
            </Stack>
          )}
        </Stack>
        {showArrow && <StyledArrow offset={parseInt(resolveCssVariable(tokens.Arrow.offset), 10)} />}
      </StyledContent>
    </FocusScope>
  );

  return (
    <Provider>
      <Root open={_visible} delayDuration={delay} onOpenChange={toggleVisible}>
        <Trigger asChild onPointerMove={onTriggerPointerMove} onFocus={onTriggerFocus} {...triggerProps}>
          {children}
        </Trigger>
        {portalled ? <Portal>{content}</Portal> : content}
      </Root>
    </Provider>
  );
}

export const renderWithTooltip = (
  content: ReactNode,
  tooltip?: ReactNode | string | Omit<TooltipProps, 'children'>
) => {
  if (isNil(tooltip)) return content;

  const tooltipProps =
    isValidElement(tooltip) || typeof tooltip === 'string'
      ? ({
          title: tooltip,
          placement: 'top',
          isMarkdown: 'title',
          overlayStyle: {
            maxWidth: '500px',
            maxHeight: '300px',
            overflow: 'auto',
            padding: theme.spaces.x12
          }
        } as TooltipProps)
      : (tooltip as TooltipProps);

  return <Tooltip {...tooltipProps}>{content}</Tooltip>;
};
